import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Amplify } from 'aws-amplify'
import { useFeatures } from 'flagged'
import { useTheme } from '../../../../components/mui/Styles/index'
import packageJson from '../../../../../package.json'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  TextField,
} from '../../../../components/mui/Core/index'
import { SignIn } from '../../../../api/userauthentication/signin'
import { GenericSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar'
import FormHeading from '../../../../components/userauthentication/FormHeading'
import awsconfig from '../../../../aws-exports'
import { setAuthenticatedUser } from './log-in-slice'
import { setSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar-slice'
import { transformCognitoUserData } from '../../../../utils/aws-cognito-utils'
import { persistor } from '../../../../app/store'
import { amplifyLoginExceptions } from '../../../../constants/amplify-api-constants'
import useBrowserTabTitles from '../../../../hooks/browserTabTitles/browser-tab-titles'
import { createTableauUser } from '../../../../api/reporting/tableau/create-tableau-user'
import { addTableauUserToGroup } from '../../../../api/reporting/tableau/add-tableau-user-to-group'
import { tableauUsers } from '../../../../constants/tableau-api-constants'
import { getTableauCredentials } from '../../../../api/reporting/tableau/tableau-get-credentials'
import { authenticateUser } from '../../../../api/user/authentication'
import { loginChallengeTypes } from '../../../../constants/challenge-type-constants'
import { routePaths } from '../../../../constants/routes'
import * as Constants from '../../../../constants/en.json'
import { localStorageKeyConstants } from '../../../../constants/local-storage-key-constants'
import { sessionStorageKeyConstants } from '../../../../constants/session-storage-key-constants'

Amplify.configure(awsconfig)

/* Login Page */
function Login() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const features = useFeatures()
  const location = useLocation()
  const theme = useTheme()
  const locationStateException = location.state?.exception
  const isUserServiceEnabled = features.isUserServiceEnabled
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  /* Verify if login attempts count has been initialized */
  const [initializedLoginAttemptsCount, setInitializedLoginAttemptsStoredCount] = useState(false)
  /* Get current stored count from local storage */
  const storedLoginAttemptsCount = parseInt(
    sessionStorage.getItem(sessionStorageKeyConstants.LOGIN_ATTEMPTS_COUNT),
    10,
  )

  const handleForgotPassword = (event) => {
    event.preventDefault()
    navigate('/reset-password')
  }

  useBrowserTabTitles(Constants.login.LOGIN_TITLE)

  useEffect(() => {
    /* Initialize login attempt count once on app load
       or if it cannot be found in local storage */
    if (!setInitializedLoginAttemptsStoredCount || !storedLoginAttemptsCount) {
      sessionStorage.setItem(sessionStorageKeyConstants.LOGIN_ATTEMPTS_COUNT, 0)
      setInitializedLoginAttemptsStoredCount(true)
    }
  }, [initializedLoginAttemptsCount, storedLoginAttemptsCount])

  useEffect(() => {
    if (locationStateException?.message) {
      setSnackbar({ message: locationStateException.message, severity: 'error', open: true })
      /* Reloads login route with empty state to prevent the location
         state error from being displayed on every page reload */
      navigate(routePaths.LOGIN, { replace: true })
    }
  }, [locationStateException, navigate])

  const handleAuthenticateUser = async (e) => {
    setIsLoggingIn(true)
    e.preventDefault()
    if (isUserServiceEnabled) {
      const request = {
        username: email,
        password,
      }
      authenticateUser(request)
        .then((response) => {
          const challengeType = response.data?.data?.challengeType
          const challengeName = response.data?.data?.challengeName
          const requireMFA =
            challengeType === loginChallengeTypes.SOFTWARE_TOKEN_MFA_CODE ||
            challengeType === loginChallengeTypes.SMS_MFA_CODE
          setIsLoggingIn(false)
          if (requireMFA) {
            const mfaData = {
              sessionId: response.data?.data?.sessionId,
              userName: response.data?.data?.username,
              challengeType,
              challengeName,
            }
            navigate(routePaths.ENTER_MFA, { state: mfaData })
          } else {
            const loginError = { message: Constants.login.MFA_NOT_SETUP_MESSAGE }
            throw loginError
          }
        })
        .catch((exception) => {
          setIsLoggingIn(false)
          const message = exception?.message || Constants.login.LOGIN_FALLBACK_MESSAGE
          dispatch(setSnackbar({ message, severity: 'error', action: true }))
        })
    } else {
      SignIn(email, password)
        .then(async (response) => {
          const tokenData = {
            idToken: response.signInUserSession.idToken.jwtToken,
            accessToken: response.signInUserSession.accessToken.jwtToken,
            refreshToken: response.signInUserSession.refreshToken.token,
          }
          const authenticatedUser = transformCognitoUserData(response)
          onAuthenticationSuccess({ authenticatedUser, tokenData })
          return authenticatedUser
        })
        .then((authenticatedUser) => {
          /* Validate Tableau User. */
          setupTableauUser(authenticatedUser)
        })
        .catch((exception) => {
          setIsLoggingIn(false)
          const error = String(exception)
          if (error === 'UserNotFoundException: User does not exist.') {
            dispatch(
              setSnackbar({ message: 'User does not exist.', severity: 'error', action: true }),
            )
          }
          if (error === 'NotAuthorizedException: Incorrect username or password.') {
            /* When user email is valid, but incorrect password...
             update login attempts count */
            const updatedLoginAttemptCount = storedLoginAttemptsCount + 1
            sessionStorage.setItem(
              sessionStorageKeyConstants.LOGIN_ATTEMPTS_COUNT,
              updatedLoginAttemptCount,
            )
            dispatch(
              setSnackbar({
                message: `Incorrect password. You have used ${updatedLoginAttemptCount} out of 6 attempts.`,
                severity: 'error',
                action: true,
              }),
            )
          } else if (error === 'NotAuthorizedException: Password attempts exceeded') {
            /* Set stored count back to 0 as after AWS allows
             user to login again, it will start from 0. */
            sessionStorage.setItem(sessionStorageKeyConstants.LOGIN_ATTEMPTS_COUNT, 0)
            dispatch(
              setSnackbar({
                message: 'Too many login attempts.  Please try again in 10 minutes.',
                severity: 'error',
                action: true,
              }),
            )
          } else if (
            error === 'PasswordResetRequiredException: Password reset required for the user'
          ) {
            dispatch(
              setSnackbar({
                message: 'Password reset required for the user',
                severity: 'error',
                action: true,
              }),
            )
            navigate('/reset-password-confirm')
          } else if (error === amplifyLoginExceptions.USER_IS_DISABLED) {
            dispatch(
              setSnackbar({
                message: `The account associated with ${email} has been deactivated. Please reach out to your account manager for assistance.`,
                severity: 'error',
                action: true,
              }),
            )
          }
        })
    }
  }

  function onAuthenticationSuccess({ authenticatedUser, tokenData }) {
    /* Store Auth Tokens in Session storage */
    sessionStorage.setItem(sessionStorageKeyConstants.ID_TOKEN, tokenData.idToken)
    sessionStorage.setItem(sessionStorageKeyConstants.ACCESS_TOKEN, tokenData.accessToken)
    sessionStorage.setItem(sessionStorageKeyConstants.REFRESH_TOKEN, tokenData.refreshToken)
    /* Reset login attempts account when the user
           has successfully logged in.  */
    sessionStorage.setItem(sessionStorageKeyConstants.LOGIN_ATTEMPTS_COUNT, 0)
    /* Set User/App details in Local Storage */
    localStorage.setItem(localStorageKeyConstants.APP_VERSION, packageJson.version)
    localStorage.setItem(localStorageKeyConstants.STORED_USERNAME, authenticatedUser.email)
    localStorage.setItem(localStorageKeyConstants.CLIENT_CODE, authenticatedUser.clientCode)
    localStorage.setItem(localStorageKeyConstants.WAREHOUSE_CODE, authenticatedUser.whse_code)
    localStorage.setItem(localStorageKeyConstants.DMS_FLAG, authenticatedUser.dms)
    localStorage.setItem(localStorageKeyConstants.API_BASE_URL, import.meta.env.VITE_BASE_URL)
    dispatch(setAuthenticatedUser(authenticatedUser))
    /* When redux state is purged from local storage during logout,
       the persisted state only gets set again if the app is refreshed
       or opened in a new tab. If the user logout and login again, then
       need to run persist to start persisting again */
    persistor.persist()
  }

  function setupTableauUser(user) {
    /* Validate Tableau User. */
    const userEmail = user?.email
    if (userEmail) {
      getTableauCredentials(userEmail)
        .then((response) => {
          setIsLoggingIn(false)
          sessionStorage.setItem(
            sessionStorageKeyConstants.TABLEAU_TOKEN,
            response?.data?.data?.token,
          )
          localStorage.setItem(
            localStorageKeyConstants.WMS_TABLEAU_DASHBOARD_URL,
            response?.data?.data?.dashboardURLs?.wmsDashboardURL,
          )
          localStorage.setItem(
            localStorageKeyConstants.DMS_TABLEAU_DASHBOARD_URL,
            response?.data?.data?.dashboardURLs?.dmsDashboardURL,
          )
          localStorage.setItem(
            localStorageKeyConstants.STATE_TABLEAU_DASHBOARD_URL,
            response?.data?.data?.dashboardURLs?.stateDashboardURL,
          )
          navigate('/dashboard')
        })
        .catch((exception) => {
          const errorCode = exception?.code
          if (errorCode === 'TABLEAU_USER_NOT_FOUND') {
            createTableauUser(userEmail, tableauUsers.type.Viewer)
              .then(() => {
                addTableauUserToGroup(userEmail, 'Client Account Managers')
                  .then(() => {
                    setIsLoggingIn(false)
                    navigate('/dashboard')
                  })
                  .catch(() => {
                    setIsLoggingIn(false)
                    navigate('/dashboard')
                  })
              })
              .catch(() => {
                navigate('/dashboard')
              })
          }
          /* bypassing - remove when done with logout testing */
          setIsLoggingIn(false)
          navigate('/dashboard')
        })
    }
    setIsLoggingIn(false)
  }

  const updateTextInput = (e, field) => {
    if (field === 'email') {
      setEmail(e.target.value)
    } else if (field === 'password') {
      setPassword(e.target.value)
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter') {
      handleAuthenticateUser(e)
    }
  }

  const isFormValid = !email || !password

  const loginBoxStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const loginTextFieldStyle = { width: '240px' }
  const loginButtonStyle = {
    mt: 3,
    mb: 5,
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  }
  const forgotPasswordBoxStyle = {
    typography: 'body1',
    '& > :not(style) + :not(style)': { ml: 2 },
  }

  return (
    <Box>
      <Backdrop open={isLoggingIn}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Box sx={theme.soloContainer}>
        <Card className='soloCard'>
          <CardContent>
            <Box sx={loginBoxStyle}>
              <FormHeading formHeader='Portal Log In' />
              <TextField
                onChange={(e) => {
                  updateTextInput(e, 'email')
                }}
                onKeyDown={(e) => handleKeyPress(e)}
                required
                label={Constants.login.EMAIL}
                variant='outlined'
                type='email'
                placeholder={Constants.login.ENTER_EMAIL}
                value={email}
                sx={loginTextFieldStyle}
              />
              <TextField
                onChange={(e) => {
                  updateTextInput(e, 'password')
                }}
                onKeyDown={(e) => handleKeyPress(e)}
                required
                label={Constants.login.PASSWORD}
                variant='outlined'
                type='password'
                placeholder={Constants.login.ENTER_PASSWORD}
                value={password}
                sx={loginTextFieldStyle}
              />
              <Button
                data-testid='login-button'
                onClick={handleAuthenticateUser}
                disabled={isFormValid}
                variant='contained'
                sx={loginButtonStyle}
              >
                {Constants.login.LOGIN}
              </Button>
              <Box sx={forgotPasswordBoxStyle} onClick={handleForgotPassword}>
                <Link href='/reset-password' variant='link' gutterBottom>
                  {Constants.login.FORGOT_PASSWORD}
                </Link>
              </Box>
            </Box>
          </CardContent>
          <GenericSnackbar />
        </Card>
      </Box>
    </Box>
  )
}

export default Login
